import React, { FormEvent, useRef, useState } from 'react'
import { useForm, usePage, router } from '@inertiajs/react'
import { useReturnTo, appendReturnTo } from '@/contexts/ReturnToContext'
import * as yup from 'yup'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import type { Task } from '@/components/tasks/TaskCard'

const STATUS_COLORS: Record<string, string> = {
  pending: '#6B7280',
  planning: '#F59E0B',
  planned: '#D97706',
  in_progress: '#3B82F6',
  in_review: '#8B5CF6',
  completed: '#10B981',
  accepted: '#059669',
  rejected: '#EF4444',
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  planning: 'Planning',
  planned: 'Planned',
  in_progress: 'In Progress',
  in_review: 'In Review',
  completed: 'Completed',
  accepted: 'Accepted',
  rejected: 'Rejected',
}

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface EditTask extends Task {
  allowed_transitions?: string[]
}

interface Props {
  open: boolean
  onClose: () => void
  boardSlug: string
  agents: AgentRef[]
  task?: EditTask
}

const requiredId = (label: string) =>
  yup.number().transform((value, original) => (original === '' ? undefined : value)).required(`${label} is required`)

const createSchema = yup.object({
  task: yup.object({
    summary: yup.string().required('Summary is required').max(255, 'Summary must be at most 255 characters'),
    instructions: yup.string().nullable().max(50000, 'Instructions must be at most 50,000 characters'),
    assignee_id: yup.mixed().nullable(),
    requestor_id: requiredId('Requestor'),
    reviewer_id: requiredId('Reviewer'),
  })
})

const editSchema = yup.object({
  task: yup.object({
    summary: yup.string().required('Summary is required').max(255, 'Summary must be at most 255 characters'),
    instructions: yup.string().nullable().max(50000, 'Instructions must be at most 50,000 characters'),
    status: yup.string().required('Status is required'),
    assignee_id: yup.mixed().nullable(),
    requestor_id: requiredId('Requestor'),
    reviewer_id: requiredId('Reviewer'),
  })
})

export function TaskFormDialog({ open, onClose, boardSlug, agents, task }: Props) {
  const returnTo = useReturnTo()
  const { current_agent } = usePage().props as { current_agent?: { id: number } }
  const isEdit = !!task
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)

  const { data, setData, put, processing, errors, setError, clearErrors, reset } = useForm({
    task: {
      summary: task?.summary ?? '',
      instructions: task?.instructions ?? '',
      status: task?.status ?? 'pending',
      assignee_id: task?.assignee?.id ?? ('' as number | ''),
      requestor_id: task?.requestor?.id ?? current_agent?.id ?? ('' as number | ''),
      reviewer_id: task?.reviewer?.id ?? task?.requestor?.id ?? current_agent?.id ?? ('' as number | ''),
    },
  })

  const allowedTransitions = task?.allowed_transitions ?? []
  const statusOptions = isEdit
    ? [task!.status, ...allowedTransitions]
    : ['pending']

  function handleAddFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const newFiles = Array.from(e.target.files || [])
    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles])
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleRemoveFile(index: number) {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()

    const schema = isEdit ? editSchema : createSchema
    try {
      schema.validateSync(data, { abortEarly: false })
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {}
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message
        })
        setError(validationErrors)
        return
      }
    }

    if (isEdit) {
      put(appendReturnTo(`/tasks/boards/${boardSlug}/tasks/${task.id}`, returnTo), {
        onSuccess: () => onClose(),
      })
    } else {
      // For create, use FormData to support file attachments
      const formData = new FormData()
      formData.append('task[summary]', data.task.summary)
      formData.append('task[instructions]', data.task.instructions)
      if (data.task.assignee_id !== '') {
        formData.append('task[assignee_id]', String(data.task.assignee_id))
      }
      formData.append('task[requestor_id]', String(data.task.requestor_id))
      formData.append('task[reviewer_id]', String(data.task.reviewer_id))
      files.forEach(f => formData.append('attachments[]', f))

      setSubmitting(true)
      router.post(
        appendReturnTo(`/tasks/boards/${boardSlug}/tasks`, returnTo),
        formData as any,
        {
          forceFormData: true,
          onSuccess: () => {
            reset()
            setFiles([])
            onClose()
          },
          onFinish: () => setSubmitting(false),
        }
      )
    }
  }

  function handleDelete() {
    if (task && confirm('Are you sure you want to delete this task? This cannot be undone.')) {
      router.delete(appendReturnTo(`/tasks/boards/${boardSlug}/tasks/${task.id}`, returnTo))
      onClose()
    }
  }

  function handleClose() {
    setFiles([])
    onClose()
  }

  const isProcessing = processing || submitting

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle>{isEdit ? 'Edit task' : 'New task'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Summary"
            fullWidth
            autoFocus
            value={data.task.summary}
            onChange={e => setData('task', { ...data.task, summary: e.target.value })}
            error={'task.summary' in errors}
            helperText={(errors as Record<string, string>)['task.summary']}
            sx={{ mt: 1, mb: 2.5 }}
          />
          <TextField
            label="Instructions"
            fullWidth
            multiline
            rows={4}
            value={data.task.instructions}
            onChange={e => setData('task', { ...data.task, instructions: e.target.value })}
            error={'task.instructions' in errors}
            helperText={(errors as Record<string, string>)['task.instructions']}
            sx={{ mb: 2.5 }}
          />
          {!isEdit && (
            <Box sx={{ mb: 2.5 }}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={handleAddFiles}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Attach files
              </Button>
              {files.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {files.map((f, i) => (
                    <Chip
                      key={`${f.name}-${i}`}
                      label={f.name}
                      size="small"
                      variant="outlined"
                      onDelete={() => handleRemoveFile(i)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
          {isEdit && statusOptions.length > 1 && (
            <FormControl fullWidth sx={{ mb: 2.5 }} error={'task.status' in errors}>
              <InputLabel>Status</InputLabel>
              <Select
                value={data.task.status}
                label="Status"
                onChange={e => setData('task', { ...data.task, status: e.target.value })}
              >
                {statusOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: STATUS_COLORS[s], flexShrink: 0 }} />
                      {STATUS_LABELS[s] || s}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {'task.status' in errors && (
                <FormHelperText>{(errors as Record<string, string>)['task.status']}</FormHelperText>
              )}
            </FormControl>
          )}
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Assignee (optional)</InputLabel>
            <Select
              value={data.task.assignee_id}
              label="Assignee (optional)"
              onChange={e => setData('task', { ...data.task, assignee_id: e.target.value as number | '' })}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'task.requestor_id' in errors}>
            <InputLabel>Requestor</InputLabel>
            <Select
              value={data.task.requestor_id}
              label="Requestor"
              onChange={e => setData('task', { ...data.task, requestor_id: e.target.value as number })}
            >
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
            {'task.requestor_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['task.requestor_id']}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'task.reviewer_id' in errors}>
            <InputLabel>Reviewer</InputLabel>
            <Select
              value={data.task.reviewer_id}
              label="Reviewer"
              onChange={e => setData('task', { ...data.task, reviewer_id: e.target.value as number })}
            >
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
            {'task.reviewer_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['task.reviewer_id']}</FormHelperText>
            )}
          </FormControl>
          {isEdit && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Danger zone
              </Typography>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={handleDelete}
              >
                Delete this task
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isProcessing}>
            {isEdit ? 'Update task' : 'Create task'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
